import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  AlertCircle,
  CheckCircle,
  HelpCircle,
  BookOpen,
  Shield,
  ChevronDown,
} from 'lucide-react';
import { faqs, symptoms, safetyTips } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const ResourcesPage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge badge-teal mb-4">Patient Resources</span>
            <h1 className="heading-hero mb-6">Understanding Dysphagia</h1>
            <p className="body-large">
              Learn about swallowing disorders, recognize the signs, and discover 
              how to stay safe while eating.
            </p>
          </div>
        </div>
      </section>

      {/* What is Dysphagia */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
              <div className="lg:col-span-3">
                <span className="badge badge-teal mb-4">Overview</span>
                <h2 className="heading-1 mb-6">What is Dysphagia?</h2>
                <div className="space-y-4 body-medium text-gray-600">
                  <p>
                    Dysphagia (dis-FAY-juh) is the medical term for difficulty swallowing. 
                    It can affect any stage of the swallowing process—from putting food in 
                    your mouth to getting it safely into your stomach.
                  </p>
                  <p>
                    Swallowing seems simple, but it's actually a complex process involving 
                    over 50 muscles and many nerves. When any part of this system is 
                    disrupted, swallowing difficulties can occur.
                  </p>
                  <p>
                    Left untreated, dysphagia can lead to serious complications including 
                    malnutrition, dehydration, and aspiration pneumonia—when food or liquid 
                    enters the lungs instead of the stomach.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-teal-50 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertCircle className="text-teal-600" size={24} />
                    <h3 className="font-semibold text-gray-900">Did You Know?</h3>
                  </div>
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      Over 15 million Americans have dysphagia
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      1 in 25 adults experience swallowing problems annually
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      Dysphagia affects up to 50% of stroke survivors
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600 font-bold">•</span>
                      Many cases are treatable with proper therapy
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms Checklist */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="badge badge-teal mb-4">Self-Assessment</span>
              <h2 className="heading-1 mb-4">Signs You May Have a Swallowing Problem</h2>
              <p className="body-medium text-gray-600">
                If you experience any of these symptoms regularly, consider seeking evaluation.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {symptoms.map((symptom, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertCircle size={14} className="text-amber-600" />
                    </div>
                    <span className="body-medium">{symptom}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="body-small text-gray-500 mb-4">
                  Experiencing these symptoms? Get a professional evaluation.
                </p>
                <button
                  onClick={() => setIsBookingOpen(true)}
                  className="btn-primary"
                >
                  Schedule an Evaluation
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Tips */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="badge badge-teal mb-4">Safety Guidelines</span>
              <h2 className="heading-1 mb-4">Tips for Safer Eating</h2>
              <p className="body-medium text-gray-600">
                Follow these guidelines to reduce your risk of swallowing difficulties.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {safetyTips.map((tip, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield size={20} className="text-teal-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                  <p className="body-small text-gray-600">{tip.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="badge badge-teal mb-4">FAQs</span>
              <h2 className="heading-1 mb-4">Frequently Asked Questions</h2>
              <p className="body-medium text-gray-600">
                Find answers to common questions about dysphagia and treatment.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl overflow-hidden border border-gray-100"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                    className="w-full p-5 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-3">
                      <HelpCircle size={20} className="text-teal-600 flex-shrink-0" />
                      <span className="font-medium text-gray-900">{faq.question}</span>
                    </div>
                    <ChevronDown
                      size={20}
                      className={`text-gray-400 transition-transform duration-300 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-8 text-gray-600 body-small leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <BookOpen size={48} className="text-teal-600 mx-auto mb-6" />
            <h2 className="heading-1 mb-4">Have More Questions?</h2>
            <p className="body-large mb-8">
              Our team is here to answer your questions and help you understand 
              your swallowing health.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsBookingOpen(true)}
                className="btn-primary"
              >
                Book a Consultation
                <ArrowRight size={16} />
              </button>
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} sourcePage="resources" />
    </>
  );
};

export default ResourcesPage;
